import React, { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Tag } from 'lucide-react';
import { formatCurrency, formatDate } from '@/lib/utils-helpers';

export default function AdminPromos() {
  const [promos, setPromos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editPromo, setEditPromo] = useState(null);
  const [deletePromo, setDeletePromo] = useState(null);
  const [form, setForm] = useState({ 
    code: '', 
    description: '',
    discount_type: 'percentage', 
    discount_value: '', 
    min_order_value: '0',
    max_discount: '',
    usage_limit: '', 
    per_user_limit: '',
    valid_from: '',
    valid_until: '',
    is_active: true 
  });

  const fetchPromos = async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/promo-codes');
      setPromos(res.data.promo_codes || res.data.promos || []);
    } catch (err) {
      if (err.response?.status === 404) {
        toast.error('Promo codes feature not available on this backend');
        setPromos([]);
      } else {
        toast.error('Failed to load promo codes');
      }
    }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchPromos(); }, []);

  const resetForm = () => setForm({ 
    code: '', 
    description: '',
    discount_type: 'percentage', 
    discount_value: '', 
    min_order_value: '0',
    max_discount: '',
    usage_limit: '', 
    per_user_limit: '',
    valid_from: '',
    valid_until: '',
    is_active: true 
  });

  const handleCreate = async () => {
    try {
      const payload = { 
        ...form, 
        discount_value: Number(form.discount_value), 
        min_order_value: form.min_order_value ? Number(form.min_order_value) : null,
        max_discount: form.max_discount ? Number(form.max_discount) : null,
        usage_limit: form.usage_limit ? Number(form.usage_limit) : null,
        per_user_limit: form.per_user_limit ? Number(form.per_user_limit) : null
      };
      await api.post('/admin/promo-codes', payload);
      toast.success('Promo code created'); 
      setShowCreate(false); 
      resetForm(); 
      fetchPromos();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create promo code');
    }
  };

  const handleEdit = async () => {
    try {
      await api.put(`/admin/promo-codes/${editPromo.id}`, { ...form, discount_value: Number(form.discount_value), min_order_amount: Number(form.min_order_amount), max_uses: form.max_uses ? Number(form.max_uses) : null });
      toast.success('Updated'); setEditPromo(null); resetForm(); fetchPromos();
    } catch (err) {
      if (err.response?.status === 404) {
        toast.error('Promo codes feature not available on this backend');
      } else {
        toast.error('Failed to update');
      }
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/admin/promo-codes/${deletePromo.id}`);
      toast.success('Deleted'); setDeletePromo(null); fetchPromos();
    } catch (err) {
      if (err.response?.status === 404) {
        toast.error('Promo codes feature not available on this backend');
      } else {
        toast.error('Failed to delete');
      }
    }
  };

  const openEdit = (promo) => {
    setEditPromo(promo);
    setForm({ 
      code: promo.code, 
      description: promo.description || '',
      discount_type: promo.discount_type, 
      discount_value: String(promo.discount_value), 
      min_order_value: promo.min_order_value ? String(promo.min_order_value) : '0',
      max_discount: promo.max_discount ? String(promo.max_discount) : '',
      usage_limit: promo.usage_limit ? String(promo.usage_limit) : '', 
      per_user_limit: promo.per_user_limit ? String(promo.per_user_limit) : '',
      valid_from: promo.valid_from || '',
      valid_until: promo.valid_until || '',
      is_active: promo.is_active 
    });
  };

  const PromoForm = ({ onSubmit, title }) => (
    <>
      <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
      <div className="space-y-4 py-4">
        <div className="space-y-2"><Label>Code</Label><Input value={form.code} onChange={e => setForm({...form, code: e.target.value.toUpperCase()})} placeholder="SUMMER25" data-testid="promo-code-input" /></div>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Type</Label>
            <Select value={form.discount_type} onValueChange={v => setForm({...form, discount_type: v})}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="percentage">Percentage</SelectItem><SelectItem value="fixed">Fixed Amount</SelectItem></SelectContent>
            </Select>
          </div>
          <div className="space-y-2"><Label>Value</Label><Input type="number" value={form.discount_value} onChange={e => setForm({...form, discount_value: e.target.value})} placeholder={form.discount_type === 'percentage' ? '10' : '5.00'} /></div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2"><Label>Min Order ($)</Label><Input type="number" value={form.min_order_amount} onChange={e => setForm({...form, min_order_amount: e.target.value})} /></div>
          <div className="space-y-2"><Label>Max Uses</Label><Input type="number" value={form.max_uses} onChange={e => setForm({...form, max_uses: e.target.value})} placeholder="Unlimited" /></div>
        </div>
        <div className="flex items-center gap-2">
          <Switch checked={form.is_active} onCheckedChange={v => setForm({...form, is_active: v})} />
          <Label>Active</Label>
        </div>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={() => { setShowCreate(false); setEditPromo(null); resetForm(); }}>Cancel</Button>
        <Button onClick={onSubmit} data-testid="promo-save-button">Save</Button>
      </DialogFooter>
    </>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-semibold tracking-tight" data-font="display">Promo Codes</h1>
          <p className="text-muted-foreground mt-1">Create and manage promotional codes.</p>
        </div>
        <Button onClick={() => { resetForm(); setShowCreate(true); }} data-testid="create-promo-button">
          <Plus className="h-4 w-4 mr-2" /> Create Code
        </Button>
      </div>

      <Card>
        <CardContent className="p-4">
          {loading ? <div className="space-y-3">{[1,2,3].map(i => <Skeleton key={i} className="h-16" />)}</div> : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="promos-table">
                <thead><tr className="border-b text-muted-foreground"><th className="text-left py-3 px-2 font-medium">Code</th><th className="text-left py-3 px-2 font-medium">Discount</th><th className="text-left py-3 px-2 font-medium">Min Order</th><th className="text-left py-3 px-2 font-medium">Uses</th><th className="text-left py-3 px-2 font-medium">Status</th><th className="text-right py-3 px-2 font-medium">Actions</th></tr></thead>
                <tbody>
                  {promos.map(promo => (
                    <tr key={promo.id} className="border-b last:border-0 hover:bg-muted/50">
                      <td className="py-3 px-2"><div className="flex items-center gap-2"><Tag className="h-4 w-4 text-primary" /><span className="font-mono font-medium">{promo.code}</span></div></td>
                      <td className="py-3 px-2">{promo.discount_type === 'percentage' ? `${promo.discount_value}%` : formatCurrency(promo.discount_value)}</td>
                      <td className="py-3 px-2">{formatCurrency(promo.min_order_value || 0)}</td>
                      <td className="py-3 px-2 tabular-nums">{promo.current_uses || 0} / {promo.max_uses || '∞'}</td>
                      <td className="py-3 px-2"><Badge variant="outline" className={promo.is_active ? 'bg-emerald-50 text-emerald-800' : 'bg-slate-100 text-slate-600'}>{promo.is_active ? 'Active' : 'Inactive'}</Badge></td>
                      <td className="py-3 px-2 text-right">
                        <div className="flex justify-end gap-1">
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(promo)}><Pencil className="h-3.5 w-3.5" /></Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => setDeletePromo(promo)}><Trash2 className="h-3.5 w-3.5" /></Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {promos.length === 0 && <tr><td colSpan={6} className="py-8 text-center text-muted-foreground">No promo codes found</td></tr>}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={showCreate} onOpenChange={setShowCreate}><DialogContent><PromoForm onSubmit={handleCreate} title="Create Promo Code" /></DialogContent></Dialog>
      <Dialog open={!!editPromo} onOpenChange={() => { setEditPromo(null); resetForm(); }}><DialogContent><PromoForm onSubmit={handleEdit} title="Edit Promo Code" /></DialogContent></Dialog>
      <AlertDialog open={!!deletePromo} onOpenChange={() => setDeletePromo(null)}>
        <AlertDialogContent><AlertDialogHeader><AlertDialogTitle>Delete Promo Code</AlertDialogTitle><AlertDialogDescription>Are you sure you want to delete {deletePromo?.code}?</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground">Delete</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
